import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { Category, ClientStore, Offer, OrderItem } from 'src/app/stores/stores';
import { SelectedStoreState } from '../../+state/stores.reducer';
import { CheckoutService } from '../../store-checkout/checkout.service';
import { LocationService } from 'src/app/public/location.service';
import { DomSanitizer } from '@angular/platform-browser';
import { getCurrentCartStatus, getSelectedStore, getSelectedStoreStatus } from '../../+state/stores.selectors';
import { takeUntil } from 'rxjs/operators';
import OfferUtils from '../../utils/OfferUtils';
import { HelperService } from 'src/app/public/helper.service';
import UrlUtils from 'src/app/stores/utils/url-utils';

@Component({
  selector: "app-catalog-layout-tiles",
  templateUrl: "./catalog-layout-tiles.component.html",
  styleUrls: ["./catalog-layout-tiles.component.scss"],
})
export class CatalogLayoutTilesComponent
  implements OnInit, OnDestroy, OnChanges
{
  currentStyles = {};
  @Input() menuCategories: Category[];
  @Input() selectedCat: number;
  @Input() categorySearch: string;
  @Input() cartItems: OrderItem[] = [];
  @Input() selectedStoreLocale: string;
  @Input() selectedStoreCurrency: string;
  @Input() selectedStoreCurrencySymbol: string;
  @Input() contentHeaderHeight: number;
  @Input() customerFooterHeight: number;
  @Input() safeAreaHeight: number = 0;
  @Input() searchBarTopHidden: number = 0;
  @Input() selectedCatalog: number;
  @Input() showPayOnlineButton: boolean;
  @Input() cartItemsTotalValue: number;
  @Input() totalQuantity: number;
  @Input() acceptTermVisible = false;
  @Input() isIOSApp: boolean;
  @Input() expandCategory: EventEmitter<any>;

  @Output() categorySelected = new EventEmitter<{
    categoryId: number;
    toggle: string;
  }>();
  @Output() productDetailsViewed = new EventEmitter<{
    offerId: any;
    categoryId: any;
    inStock: any;
    isExpandable: any;
    isOrderable: any;
    basketEnabled: any;
  }>();
  @Output() itemAddedToCart = new EventEmitter<{
    offerId: number;
    categoryId: number;
    inStock: boolean;
    isDirectlyOrderable: boolean;
    isExpandable: boolean;
    isOrderable: boolean;
    basketEnabled: boolean;
  }>();
  @Output() decreaseItemQty = new EventEmitter<OrderItem>();

  @Output() viewOrder = new EventEmitter<any>();
  @Output() payOnline = new EventEmitter<any>();

  selectedStore$: Observable<ClientStore>;
  selectedStore: ClientStore;
  selectedStoreStatus$: Observable<string>;
  unsubscribe$: Subject<void> = new Subject<void>();
  currentCartStatus: string;
  basketEnabled = false;
  orderingEnabled = false;
  isAdminOrderCaptureUpdate: boolean;
  isMobileApp: boolean = false;
  isCategoryAction = false;
  scrollTimeout: any;

  constructor(
    private store: Store<SelectedStoreState>,
    public checkoutService: CheckoutService,
    private locationService: LocationService,
    public helper: HelperService,
    private sanitizer: DomSanitizer,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.isMobileApp = this.helper.isMobileApp();
    this.selectedStore$ = this.store.pipe(select(getSelectedStore));
    this.selectedStoreStatus$ = this.store.pipe(select(getSelectedStoreStatus));
    this.isAdminOrderCaptureUpdate =
      this.locationService.isAdminOrderCaptureUpdate();

    this.selectedStore$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedStore) => {
        this.selectedStore = selectedStore;
        this.basketEnabled =
          this.locationService.isBasketEnabled() == null
            ? this.selectedStore?.settings?.BASKET_ENABLED
            : this.locationService.isBasketEnabled();
        this.orderingEnabled = selectedStore?.settings?.ENABLE_ORDERING;
      });

    this.store
      .pipe(select(getCurrentCartStatus))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((cartStatus) => {
        this.currentCartStatus = cartStatus;
      });

    this.expandCategory.subscribe(({ categoryId, toggle }) =>
      this.onExpandCategory(categoryId, toggle),
    );
    this.checkUrl();
  }

  
  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedCat) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.scrollToTab(), 150);
    }
  }

  scrollToTab() {
    const tabElement = document.getElementById(`tab${this.selectedCat}`);
    const scrollablePanel = document.getElementById("scrollable-panel");
    if (tabElement && scrollablePanel) {
      scrollablePanel.scrollTop = tabElement.offsetTop; 
    }
  }

  isAllergenSelected(attributes, name) {
    const attr = attributes.find(item => item.key === name);
    return attr ? attr.value : false;
  }

  checkUrl() {
    const currentUrl = this.router.url; // Get the current URL from Router
    this.isCategoryAction = UrlUtils.checkUrl(currentUrl);
  }

  getBackgroundImage(url: any) {
    if (url) {
      return this.sanitizer.bypassSecurityTrustStyle(`url('${url}')`);
    }
    return "";
  }

  viewProductDetails(
    offerId: any,
    categoryId: any,
    inStock: any,
    isExpandable: any,
    isOrderable: any,
    basketEnabled: any,
  ) {
    this.productDetailsViewed.emit({
      offerId,
      categoryId,
      inStock,
      isExpandable,
      isOrderable,
      basketEnabled,
    });
  }

  onSelectCategory(categoryId: number, toggle = "off") {
    this.categorySelected.emit({ categoryId, toggle });
  }

  getCartItems(menuItem: any): OrderItem[] {
    return this.cartItems?.filter((item) => item.offerId === menuItem?.offerId);
  }

  getFilteredCategories(): Category[] {
    return this.menuCategories?.filter(
      (category) => this.getOffers(category)?.length > 0,
    );
  }

  getOffers(menuItem: Category) {
    return menuItem?.offers?.filter(
      (offer) =>
        !this.categorySearch ||
        offer.name?.toLowerCase()?.includes(this.categorySearch.toLowerCase()),
    );
  }

  showPlusButton(offer: Offer): boolean {
    return OfferUtils.showPlusButton(
      offer,
      this.basketEnabled,
      this.orderingEnabled,
    );
  }

  onViewOrder(event) {
    event.stopPropagation();
    event.preventDefault();
    this.viewOrder.emit(event);
  }

  onPayOnline() {
    this.payOnline.emit();
  }

  onExpandCategory(categoryId: number, toggle: string) {
    const queryCatNav = document.querySelector(`#category${categoryId}`);
    if (toggle === "off") {
      queryCatNav?.classList?.add("expanded");
    }
    if (toggle === "toggle") {
      queryCatNav?.classList?.toggle("expanded");
    }
  }

  isCategoryExpanded(categoryId: number): boolean {
    const queryCatNav = document.querySelector(`#category${categoryId}`);
    return (
      queryCatNav?.classList?.contains("expanded") || !!this.categorySearch
    );
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onOfferImageClick(event, offer) {
    if (
      this.getCartItems(offer)?.length > 0 ||
      this.getQuantity(this.getCartItems(offer)) > 0
    ) {
      return;
    }

    event.stopPropagation();
    const {
      offerId,
      categoryId,
      inStock,
      isDirectlyOrderable,
      isExpandable,
      isOrderable,
    } = offer;
    this.itemAddedToCart.emit({
      offerId,
      categoryId,
      inStock,
      isDirectlyOrderable,
      isExpandable,
      isOrderable,
      basketEnabled: this.basketEnabled,
    });
  }

  getQuantity(items) {
    return items.reduce((acc, item) => acc + item.quantity, 0);
  }
}
